import {
  BlockStack,
  Button,
  Card,
  Checkbox,
  ContextualSaveBar,
  DescriptionList,
  Divider,
  FormLayout,
  Grid,
  InlineGrid,
  InlineStack,
  Page,
  Popover,
  Select,
  Tag,
  Text,
  TextField,
} from '@shopify/polaris';
import { useField, useForm } from '@shopify/react-form';
import QuickUploadImage from 'components/oneclick-upload-image';
import SkeletonLoading from 'components/skeletonPageLoading';
import { useNotification } from 'context/NotificationContext';
import { useSettings } from 'context/SettingsContext';
import __ from 'languages/index';
import { useClearAllSession, useSendTestEmail, useUpdateSetting } from 'queries/setting.query';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import helpers from '../../helpers';
import { useMutateGetUsers } from 'queries/users.query';
import QuickSearchUser from 'components/quickSearchUser';
import { PlusIcon } from '@shopify/polaris-icons';
import { TypedUser } from 'interface/user.model';

export default function Settings() {
  const { addNotification } = useNotification();
  const { settings: entities, loading } = useSettings();
  const { mutateAsync: updateSetting, isSuccess, isPending } = useUpdateSetting();

  const { mutateAsync: updateEntity, error, isSuccess: updateSuccess, isPending: updating } = useUpdateSetting();

  /** Hết code lấy user role ra ... */
  const useFields = {
    admin_email: useField<string>({ value: entities?.admin_email ?? '', validates: [] }),

    email_default_template: useField<string>({ value: entities?.email_default_template ?? '', validates: [] }),
    email_default_setting: useField<string>({ value: entities?.email_default_setting ?? '', validates: [] }),

    site_logo: useField<string>({ value: entities?.site_logo ?? '', validates: [] }),

    company_name: useField<string>({ value: entities?.company_name ?? '', validates: [] }),

    company_tax_code: useField<string>({ value: entities?.company_tax_code ?? '', validates: [] }),

    company_address: useField<string>({ value: entities?.company_address ?? '', validates: [] }),
    company_representative: useField<string>({ value: entities?.company_representative ?? '', validates: [] }),

    company_phonenumber: useField<string>({
      value: entities?.company_phonenumber ?? '',
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isPhoneNumber(inputVal)) return 'Số điện thoại không hợp lệ';
        },
      ],
    }),
    company_email: useField<string>({
      value: entities?.company_email ?? '',
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isEmail(inputVal)) return 'Email không hợp lệ';
        },
      ],
    }),

    order_created_notification: useField<string>({ value: entities?.order_created_notification ?? '0', validates: [] }),
  };

  const {
    fields,
    submit,
    submitting,
    dirty,
    reset: resetForm,
    submitErrors,
    makeClean,
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateEntity({
          admin_email: values.admin_email,
          email_default_template: values.email_default_template,
          email_default_setting: values.email_default_setting,
          site_logo: values.site_logo,
          company_name: values.company_name,
          company_tax_code: values.company_tax_code,
          company_address: values.company_address,
          company_representative: values.company_representative,
          company_phonenumber: values.company_phonenumber,
          company_email: values.company_email,
          order_created_notification: Number(values.order_created_notification),
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  const { mutateAsync: clearAllSession } = useClearAllSession();

  /**
   * Notification
   */

  useEffect(() => {
    if (error) {
      addNotification('error', error.message);
    }
  }, [error]);

  useEffect(() => {
    if (updateSuccess) addNotification('info', __('updated_successfully'));
  }, [updateSuccess]);

  const { mutate: getUserData, data } = useMutateGetUsers();
  const [userReceivedNotificationData, setUserReceivedNotificationData] = useState<TypedUser[]>([]);
  useEffect(() => {
    if (data) {
      let { body } = data;
      setUserReceivedNotificationData(body);
    }
  }, [data]);

  const [userAsigned, setUserAsigned] = useState([]);
  useEffect(() => {
    if (entities?.account_received_noti_for_new_order) {
      setUserAsigned(String(entities?.account_received_noti_for_new_order).split(','));
    }
  }, [entities?.account_received_noti_for_new_order]);

  const [showAddReferrer, setShowAddReferrer] = useState(false);
  const addNewReferrer = useCallback(
    async (user_id: bigint | string) => {
      let new_record = entities.account_received_noti_for_new_order ? entities.account_received_noti_for_new_order + ',' + user_id : user_id;
      await updateSetting({ account_received_noti_for_new_order: new_record });
    },
    [entities?.account_received_noti_for_new_order, updateSetting]
  );

  const removeTag = useCallback(
    async (user_id: string) => {
      let newData = userAsigned.filter((previousTag) => previousTag !== user_id);
      setUserAsigned(newData);
      await updateSetting({ account_received_noti_for_new_order: newData.join(',') });
    },
    [userAsigned]
  );

  useEffect(() => {
    if (!helpers.isEmpty(userAsigned)) {
      getUserData({ user_id: userAsigned.join(','), limit: 10, sort: 'createdAt:desc' });
    }
  }, [userAsigned]);

  const [uploadError, setUploadError] = useState(null);

  return (
    <>
      <Helmet>
        <title>Cài đặt chung</title>
      </Helmet>
      {loading ? (
        <SkeletonLoading />
      ) : (
        <Page title="Cài đặt chung">
          <DescriptionList
            items={[
              {
                term: 'Thông tin chung',
                description: (
                  <Card>
                    <BlockStack gap="400">
                      <InlineGrid columns={['oneThird', 'twoThirds']} gap={'400'}>
                        <div className="d1">
                          <QuickUploadImage
                            onSuccess={(e) => {
                              useFields.site_logo.onChange(e.media_url);
                            }}
                            onError={(e) => setUploadError(e)}
                            title="Click để tải logo lên"
                            placeholder={helpers.getMediaLink(useFields.site_logo.value)}
                          />
                          <Text as="p" tone="critical">
                            {uploadError}
                          </Text>
                        </div>
                        <div className="d2">
                          <TextField label="Tên công ty" autoComplete="off" {...fields.company_name} />
                          <TextField label="Mã số thuế" autoComplete="off" {...fields.company_tax_code} />
                        </div>
                      </InlineGrid>

                      <div className="site_main_address">
                        <BlockStack gap={'400'}>
                          <TextField label="Địa chỉ" autoComplete="off" {...fields.company_address} />
                          <TextField label="Giám đốc/đại diện doanh nghiệp" autoComplete="off" {...fields.company_representative} />
                          <FormLayout.Group>
                            <TextField
                              label="Số điện thoại công ty"
                              placeholder="Số điện thoại"
                              autoComplete="off"
                              type="tel"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              disabled={submitting}
                              clearButton
                              {...fields.company_phonenumber}
                            />
                            <TextField
                              label="Email liên hệ"
                              placeholder="@company"
                              autoComplete="off"
                              disabled={submitting}
                              clearButton
                              {...fields.company_email}
                            />
                          </FormLayout.Group>

                          <Text as="p">Những thông tin này sẽ được ghi vào trong Email và một số template khác ví dụ như invoice ...</Text>
                        </BlockStack>
                      </div>
                    </BlockStack>
                  </Card>
                ),
              },
              {
                term: 'Cài đặt nhận thông báo',
                description: (
                  <Card>
                    <BlockStack gap="400">
                      <Checkbox
                        label="Nhận thông báo khi đơn hàng mới được tạo."
                        checked={useFields.order_created_notification.value === '1'}
                        onChange={() => useFields.order_created_notification.onChange(useFields.order_created_notification.value === '0' ? '1' : '0')}
                      />

                      <Text as="h3" variant="headingSm">
                        Người nhận thông báo đơn hàng mới
                      </Text>

                      <InlineStack gap={'400'}>
                        {entities?.account_received_noti_for_new_order
                          ? userReceivedNotificationData?.map((userData, index) => {
                              return (
                                <Tag key={`jfdskg_${index}`} onRemove={() => removeTag(userData?.user_id)}>
                                  {userData.display_name}
                                </Tag>
                              );
                            })
                          : 'Chưa có ai, cài đặt ngay'}
                      </InlineStack>

                      <Popover
                        active={showAddReferrer}
                        activator={
                          <Button variant="plain" icon={PlusIcon} onClick={() => setShowAddReferrer(true)}>
                            Thêm mới tài khoản nhận thông báo
                          </Button>
                        }
                        autofocusTarget="first-node"
                        onClose={() => setShowAddReferrer(false)}
                      >
                        <QuickSearchUser
                          title="Bất kể tài khoản nào"
                          user_role=""
                          onClose={(userData: any) => {
                            addNewReferrer(userData.user_id);
                            setShowAddReferrer(false);
                          }}
                        />
                      </Popover>
                    </BlockStack>
                  </Card>
                ),
              },
              {
                term: 'Cài đặt nâng cao',
                description: (
                  <Card>
                    <BlockStack gap="400">
                      <Text as="h3">Dọn dẹp phiên làm việc</Text>
                      <Button tone="critical" loading={false} onClick={clearAllSession}>
                        Xoá toàn bộ phiên làm việc
                      </Button>
                      <Text tone="subdued" as="p">
                        Khi dọn dẹp phiên làm việc, mọi phiên làm việc hết hạn trong cơ sở dữ liệu sẽ được xóa bỏ một cách an toàn.
                      </Text>
                    </BlockStack>
                  </Card>
                ),
              },
            ]}
          />

          <Card padding={'0'}></Card>

          {dirty && (
            <ContextualSaveBar
              message="Thay đổi chưa được lưu"
              saveAction={{ onAction: () => submit(), loading: updating }}
              discardAction={{ onAction: () => resetForm() }}
            />
          )}
        </Page>
      )}
      <br />
      <br />
    </>
  );
}

